import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function PatientAuth() {
  const { patientLogin, patientRegister } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ org_id: "", full_name: "", email: "", password: "", phone: "" });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/portal/hospitals").then((r) => setHospitals(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await patientLogin({ org_id: form.org_id, email: form.email.trim(), password: form.password });
      } else {
        if (!form.org_id) { toast.error("Select your hospital"); setLoading(false); return; }
        await patientRegister({
          org_id: form.org_id, full_name: form.full_name, email: form.email.trim(),
          password: form.password, phone: form.phone || null,
        });
      }
      toast.success("Welcome to your patient portal");
      navigate("/portal");
    } catch (err) {
      toast.error(errMsg(err, "Could not sign you in"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Patient Portal"
      subtitle="Book appointments, view results, prescriptions & bills"
      image="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      footer={<>Staff member? <Link to="/login" className="font-semibold text-teal-700 hover:underline">Staff sign in</Link></>}
    >
      <div className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
        {["login", "register"].map((m) => (
          <button key={m} data-testid={`patient-tab-${m}`} onClick={() => setMode(m)}
            className={`rounded-md py-2 text-sm font-semibold capitalize transition-colors ${mode === m ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}>
            {m === "login" ? "Sign in" : "Register"}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>Hospital</Label>
          <Select value={form.org_id} onValueChange={(v) => set("org_id", v)}>
            <SelectTrigger data-testid="patient-hospital"><SelectValue placeholder="Select your hospital" /></SelectTrigger>
            <SelectContent>{hospitals.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {mode === "register" && (
          <div className="space-y-1.5"><Label>Full name</Label>
            <Input data-testid="patient-fullname" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required /></div>
        )}
        <div className="space-y-1.5"><Label>Email</Label>
          <Input data-testid="patient-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required /></div>
        {mode === "register" && (
          <div className="space-y-1.5"><Label>Phone (optional)</Label>
            <Input data-testid="patient-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
        )}
        <div className="space-y-1.5"><Label>Password</Label>
          <PasswordInput data-testid="patient-password" value={form.password} onChange={(e) => set("password", e.target.value)} required /></div>
        <Button data-testid="patient-submit" type="submit" disabled={loading} className="w-full bg-teal-700 hover:bg-teal-800">
          {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
